import React, { useEffect, useRef, useState, useCallback } from 'react';
import RealtimeVoiceClient from './realtimeVoiceClient';
import { execAgentTool } from '../utils/agentApiUtils';
import '../../../assets/scss/components/Agent/VoiceMode.scss';

/**
 * VoiceModeBar — inline live-voice control (redesign).
 *
 * Replaces the chat input row while active. It does NOT cover the chat or show
 * cards/captions itself — instead it streams transcripts and tool results up to
 * the parent so the normal chat (messages + space cards) updates live while the
 * user speaks. Shows a compact animated orb + status + mute/end.
 */
const STATUS_LABELS = {
  connecting: 'Connecting…',
  live: 'Listening',
  error: 'Voice error',
  closed: 'Ended',
};

const PHASE_LABELS = {
  listening: 'Listening…',
  thinking: 'Processing…',
  searching: 'Searching spaces…',
  speaking: 'Spacey is speaking…',
};

const VoiceModeBar = ({ userContext, voice, onClose, onCaption, onToolResult }) => {
  const clientRef = useRef(null);
  const lastSpacesRef = useRef([]);
  const lastSearchContextRef = useRef(null);
  const lastUserUtteranceRef = useRef('');
  const [status, setStatus] = useState('connecting');
  const [phase, setPhase] = useState('listening');
  const [userMuted, setUserMuted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [level, setLevel] = useState(0);
  const [retryKey, setRetryKey] = useState(0);

  const handleClose = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.stop();
      clientRef.current = null;
    }
    if (onClose) onClose();
  }, [onClose]);

  const handleRetry = useCallback(() => {
    setErrorMessage('');
    setStatus('connecting');
    setRetryKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const client = new RealtimeVoiceClient({
      onStatus: setStatus,
      onPhase: setPhase,
      onError: (err) => setErrorMessage(err?.message || 'Voice mode failed to start.'),
      onLevel: setLevel,
      onCaption: (caption) => {
        if (caption?.role === 'user' && caption?.final) {
          const utterance = (caption.text || '').trim();
          if (utterance) {
            lastUserUtteranceRef.current = utterance;
          }
        }
        if (onCaption) onCaption(caption);
      },
      executeToolCall: (name, args) => execAgentTool(name, args, userContext, {
        spaces: lastSpacesRef.current,
        searchContext: lastSearchContextRef.current,
        message: lastUserUtteranceRef.current,
      }),
      onToolResult: (result) => {
        if (result?.spaces && result.spaces.length) {
          lastSpacesRef.current = result.spaces;
        }
        if (result?.searchContext) {
          lastSearchContextRef.current = result.searchContext;
        }
        if (onToolResult) onToolResult(result);
      },
    });
    clientRef.current = client;

    client.start(userContext, voice).catch(() => {
      // Error surfaced via onStatus/onError.
    });

    return () => {
      client.stop();
      clientRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryKey]);

  const toggleMute = () => {
    const next = !userMuted;
    setUserMuted(next);
    if (clientRef.current) clientRef.current.setMuted(next);
  };

  const isLive = status === 'live';
  const isError = status === 'error';
  const isListening = isLive && phase === 'listening';
  const micLocked = isLive && phase !== 'listening';
  const orbPhase = isError ? 'error' : (isLive ? phase : 'connecting');
  const statusText = isError
    ? (errorMessage || STATUS_LABELS.error)
    : (isLive ? (PHASE_LABELS[phase] || STATUS_LABELS.live) : STATUS_LABELS[status]);
  const orbScale = 1 + Math.min(0.4, level * 0.6);

  return (
    <div className={`voice-mode-bar ${micLocked ? 'voice-mode-bar--locked' : ''}`}>
      <div className={`voice-bar-orb voice-bar-orb--${orbPhase} ${userMuted ? 'voice-bar-orb--muted' : ''}`}>
        <span className="voice-bar-orb__ring" />
        <span
          className="voice-bar-orb__core"
          style={{ transform: `scale(${orbScale.toFixed(3)})` }}
        />
      </div>

      <div className="voice-bar-status">{statusText}</div>

      <div className="voice-bar-controls">
        {isListening && (
          <button
            type="button"
            className={`voice-bar-button ${userMuted ? 'is-active' : ''}`}
            onClick={toggleMute}
            aria-label={userMuted ? 'Unmute microphone' : 'Mute microphone'}
            title={userMuted ? 'Unmute' : 'Mute'}
          >
            {userMuted ? 'Unmute' : 'Mute'}
          </button>
        )}
        {isError && (
          <button
            type="button"
            className="voice-bar-button"
            onClick={handleRetry}
            aria-label="Reconnect voice mode"
            title="Reconnect"
          >
            Reconnect
          </button>
        )}
        <button
          type="button"
          className="voice-bar-button voice-bar-button--end"
          onClick={handleClose}
          aria-label="End voice mode"
          title="End voice mode"
        >
          End
        </button>
      </div>
    </div>
  );
};

export default VoiceModeBar;
